import React, { useState } from 'react'
import { Field, Formik } from 'formik'
import * as Yup from 'yup'
import pluralize from 'pluralize'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconCheck, IconPencil, IconX } from '@tabler/icons-react'

import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { editMeasureName, editSectionName } from '@/reducers/scorecards/scorecards.actions'
import { TextField } from '@/components/forms/formik'

import './scorecards.css'

export const ScorecardEditorCard = ({ cardData, onClick, children, sectionData = false }) => {
  const { data } = useSelector((state) => state.scorecards)
  const { sid, organization_id: organizationId } = data.targetScorecardConfig
  const { ssid } = data.targetScorecardSection
  const dispatch = useDispatch()

  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(cardData.name)

  const autoFail =
    cardData && cardData.measures ? cardData.measures.filter((measure) => measure.auto_fail) : []
  const autoZero =
    cardData && cardData.measures ? cardData.measures.filter((measure) => measure.auto_zero) : []
  const criteriaLength = []
  cardData?.measures?.forEach((measure) => criteriaLength.push(measure.criteria.length))
  const measureLength = []
  cardData?.measures?.forEach((measure) => {
    if (measure.criteria.length > 0) {
      measureLength.push(measure)
    }
  })

  const toggleEdit = () => {
    if (name !== cardData.name && editing) {
      setName(cardData.name)
    }
    setEditing(!editing)
  }

  const submitNameChange = ({ name }) => {
    if (sectionData) {
      dispatch(editMeasureName(sid, ssid, cardData.smid, name, organizationId))
    } else {
      dispatch(editSectionName(sid, cardData.ssid, name, organizationId))
    }
    setEditing(false)
  }

  const initialValues = { name: cardData.name }
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .max(100, 'Name cannot be longer than 100 characters.')
      .required('Name is required'),
  })

  return (
    <Card clickable compact data-testid="scorecard-section-card" key={cardData.name}>
      {editing ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => submitNameChange(values)}
        >
          {({ handleSubmit, errors }) => (
            <form
              className="scorecard-card-header-edit"
              onSubmit={(event) => {
                event.preventDefault()

                handleSubmit()
              }}
            >
              <Field
                data-testid="edit_section_name_box"
                name="name"
                component={TextField}
                placeholder="Section Name"
              />
              <div className="flex-align-center small-gap">
                <Button
                  small
                  square
                  brand
                  type="submit"
                  disabled={!isEmpty(errors)}
                  data-testid="edit_name_submit"
                >
                  <IconCheck />
                </Button>
                <Button small square onClick={toggleEdit} data-testid="edit_name_cancel">
                  <IconX />
                </Button>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <div className="flex-align-center flex-space-between small-gap">
          <h2 data-testid="scorecard-section-name">{cardData.name}</h2>
          <Button square basic small onClick={toggleEdit} data-testid="edit_category_toggle">
            <IconPencil />
          </Button>
        </div>
      )}

      <div onClick={() => onClick(cardData)} role="button" tabIndex={0}>
        <div className="editor-card-content" data-testid="scorecard-section-message">
          {children}
          {measureLength.length > 0 && (
            <Pill emphasized small informative data-testid="criteria-badge">
              {pluralize(`Measure`, measureLength.length, true)} in use
            </Pill>
          )}
          {autoFail.length > 0 && (
            <Pill emphasized small critical data-testid="autofail-badge">
              {autoFail.length} Auto-fail
            </Pill>
          )}
          {autoZero.length > 0 && (
            <Pill emphasized small caution data-testid="autozero-badge">
              {autoZero.length} Auto-zero
            </Pill>
          )}
          {!sectionData && measureLength.length === 0 && (
            <div className="status-muted">No measures in use</div>
          )}
        </div>

        <footer className="flex-space-between">
          <Pill
            small
            outlined
            success={cardData.active}
            critical={!cardData.active}
            data-testid="scorecard-section-active"
          >
            {cardData.active ? 'Active' : 'Inactive'}
          </Pill>
          <span className="muted-text" data-testid="scorecard-section-weight">
            {cardData.weight ? `${cardData.weight}%` : ''}
          </span>
        </footer>
      </div>
    </Card>
  )
}
